import {
    useRef,
    useState
} from 'react';

import {Alert, Button, SimpleGrid, Text} from '@mantine/core';
import {I18n} from '@aws-amplify/core';
import {useVerifiedPermissions} from '@flexinet/auth-components';

import {useOrders, useOrdersFilter} from '../hooks/useOrders.ts';
import {useOrder} from '../hooks/useOrder.ts';
import {useOrdersSelection} from '../hooks/useOrdersSelection.ts';
import {useOrdersTableColumns} from '../hooks/useOrdersTableColumns.tsx';
import {FormDrawer} from '../drawers/FormDrawer.tsx';
import {rbacActions} from '../../../config/rbac/actions.ts';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';
import {Order} from '../types';

export const RecordsTable = () => {
    const {selectedRecords, setSelectedRecords} = useOrdersSelection();
    const {columns} = useOrdersTableColumns();
    const {handleClearFilters} = useOrdersFilter();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<Order | null>(null);

    // Editing is a write, so the row click that opens the edit drawer is gated on UpdateOrder,
    // as Add and Delete are on theirs. UpdateOrder is in the deployed AVP schema (FNP-1884).
    const {allowedActions} = useVerifiedPermissions();
    const canEdit = allowedActions.includes(rbacActions.UpdateOrder);

    const {
        items,
        visibleItems,
        keyword,
        filters,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useOrders();

    // The edit goes through the mutation hook: whitelisted input, a toast either way,
    // invalidation of the list. Resolves true when saved; the drawer closes only then.
    const {updateRecord} = useOrder();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const term = keyword.trim();
    const filterCount = Object.keys(filters).length;

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load orders')}>
                    {I18n.get('The billing API did not return this customer\'s orders. Try Reload; if it persists, the API may be unavailable in this environment.')}
                </Alert>
            )}

            <MantineDataTable<Order>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far, the same scope as the
                // keyword filter. Token pagination cannot reorder unseen rows.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={canEdit ? ({record}) => {
                    setSelectedRecord(record);
                    setDrawerOpened(true);
                } : undefined}
            />

            {/*
              EMPTY-ISH STATES (§13). A search with no match, a filter with no match, and
              genuinely nothing yet. Gated on !loading so they do not flash during the first
              fetch, and on !isError so a failed load keeps its own Alert. The status and
              customer filters run on the server after the page limit, so a page can come back
              empty while more pages exist: the footer's Load More stays the way forward.
            */}
            {!loading && !isError && visibleItems.length === 0 && (
                term ? (
                    <Alert color='gray' title={I18n.get('No matching orders')}>
                        {`${I18n.get('Nothing in the loaded orders matches')} "${term}". ${I18n.get('Clear the search to see every loaded row. A match may still be on a page that has not been loaded yet.')}`}
                    </Alert>
                ) : filterCount > 0 ? (
                    <Alert color='gray' title={I18n.get('No orders match the filter')}>
                        <Text size='sm'>
                            {I18n.get('The data may be there; the filter hides it. A match may also be on a page that has not been loaded yet.')}
                        </Text>
                        <Button variant='light' size='xs' mt='sm' onClick={handleClearFilters}
                                data-testid='orders-clear-filters'>
                            {I18n.get('Clear filters')}
                        </Button>
                    </Alert>
                ) : nextToken ? (
                    // The server filters by customer AFTER its page limit, so an empty page
                    // with a cursor proves nothing about the customer: say so, not "none".
                    <Alert color='gray' title={I18n.get('No orders on the pages loaded so far')}>
                        {I18n.get('More pages exist. Use Load more to keep looking.')}
                    </Alert>
                ) : (
                    <Alert color='blue' title={I18n.get('No orders yet')}>
                        {I18n.get('This customer has no orders.')}
                    </Alert>
                )
            )}

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'order'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />

            {drawerOpened && selectedRecord && (
                <FormDrawer
                    record={selectedRecord}
                    handleSubmit={updateRecord}
                    opened={drawerOpened}
                    onClose={() => {
                        setDrawerOpened(false);
                        setSelectedRecord(null);
                    }}
                />
            )}
        </SimpleGrid>
    );
};
